import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchMyOnboardings,
  MyOnboardingListItem,
} from "services/onboarding.service";
import { STEP_LABELS } from "utils/onboardingStatus";
import MyOnboardingDetail from "./my-onboarding-detail";

function MyOnboarding() {
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";

  const [list, setList] = useState<MyOnboardingListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    try {
      setError(null);
      const items = await fetchMyOnboardings();
      setList(items);
      // Auto-skip picker when only one pending onboarding remains
      if (items.length === 1) {
        setSelectedId(items[0].id);
      }
    } catch {
      setError(t("error"));
    }
  }, [t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleBack = useCallback(() => {
    setSelectedId(null);
    loadList();
  }, [loadList]);

  if (error) {
    return (
      <div className="w-full">
        <h1 className="text-2xl font-semibold mb-6">{t("my_onboarding")}</h1>
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="w-full">
        <h1 className="text-2xl font-semibold mb-6">{t("my_onboarding")}</h1>
        <div className="flex justify-center p-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="w-full">
        <h1 className="text-2xl font-semibold mb-6">{t("my_onboarding")}</h1>
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
          <p>{t("no_active_onboarding")}</p>
        </div>
      </div>
    );
  }

  if (selectedId) {
    return (
      <MyOnboardingDetail
        onboardingId={selectedId}
        onBack={list.length > 1 ? handleBack : undefined}
      />
    );
  }

  // Picker: multiple pending onboardings
  return (
    <div className="w-full">
      <h1 className="text-2xl font-semibold mb-6">{t("my_onboarding")}</h1>
      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm mb-4">
        <p>{t("multiple_onboardings_select_company")}</p>
      </div>
      <div className="flex flex-col gap-3">
        {list.map((item) => {
          const stepLabel =
            STEP_LABELS[item.status]?.[lang] || STEP_LABELS[item.status]?.en || item.status;
          const position =
            lang === "lt"
              ? item.positionLT || item.positionEN
              : item.positionEN || item.positionLT;
          return (
            <div
              key={item.id}
              className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm flex items-center justify-between gap-3 flex-wrap"
            >
              <div className="flex flex-col gap-1">
                <p className="text-lg font-semibold">
                  {item.company?.name || t("company")}
                </p>
                <p className="text-sm text-gray-600">{position || ""}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {stepLabel}
                  </span>
                </div>
              </div>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                onClick={() => setSelectedId(item.id)}
              >
                {t("continue")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MyOnboarding;
